// src/components/ranking/PlayerRanking.js
// eslint-disable-next-line no-unused-vars
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {FaMedal} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import ServerSwitch from '../serverSwitch';
import {getPlayerKillRanking, getActiveServers} from '../../../api/axios';

const getMedal = rank => {
  if (rank === 1) return <FaMedal style={{color: 'gold'}} />;
  if (rank === 2) return <FaMedal style={{color: 'silver'}} />;
  if (rank === 3) return <FaMedal style={{color: 'brown'}} />;
  return rank;
};

const PlayerKillRanking = () => {
  const {t} = useTranslation();
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sv, setSv] = useState('hybrid');

  // aktive Server laden; Dropdown nur bei count > 1
  const [activeServers, setActiveServers] = useState([]); // [{friendly_key, display_name}, ...]
  const showDropdown = activeServers.length > 1;

  useEffect(() => {
    let mounted = true;
    getActiveServers()
      .then(res => {
        if (!mounted) return;
        const list = Array.isArray(res?.data?.servers) ? res.data.servers : [];
        setActiveServers(list);

        // Wenn genau 1 aktiv ist, stelle sicher, dass wir den auch nutzen
        if (list.length === 1 && list[0]?.friendly_key && list[0].friendly_key !== sv) {
          setSv(list[0].friendly_key);
        }
        // Wenn mehrere aktiv sind, aber aktueller sv nicht dabei ist → auf ersten aktiven setzen
        if (list.length > 1 && !list.find(s => s.friendly_key === sv) && list[0]?.friendly_key) {
          setSv(list[0].friendly_key);
        }
      })
      .catch(() => {
        // Falls Liste fehlschlägt, lassen wir activeServers leer -> kein Dropdown
        setActiveServers([]);
      });
    return () => {
      mounted = false;
    };
  }, [sv]);

  useEffect(() => {
    setLoading(true);
    setRanking([]);
    const timer = setTimeout(() => {
      getPlayerKillRanking(sv)
        .then(response => {
          let data = response.data;

          if (typeof data === 'string') {
            console.error('Fehlermeldung von API erhalten:', data);
            setRanking([]); // Leeren Array setzen, um UI-Fehler zu vermeiden
            return;
          }

          // Falls die Daten kein Array sind, in ein Array umwandeln
          if (!Array.isArray(data)) {
            data = Object.values(data);
          }
          setRanking(data);
          setLoading(false);
        })
        .catch(error => {
          //console.error("Error fetching player kill ranking:", error);
          setRanking([]); // statt error string im State zu speichern
          setLoading(false);
        });
    }, 1000); // 1000ms Delay

    return () => clearTimeout(timer);
  }, [sv]);

  return (
    <RankingContainer>
      {showDropdown ? (
        <HeaderRow>
          <Title>{t('playerKillRanking.title')}</Title>
          <ServerSwitch
            value={sv}
            onChange={setSv}
            // optional: Labels aus der aktiven Liste übernehmen
            options={activeServers.map(s => ({
              value: s.friendly_key,
              label: s.display_name || s.friendly_key,
            }))}
          />
        </HeaderRow>
      ) : (
        <Title>{t('playerKillRanking.title')}</Title>
      )}
      {loading && ranking.length === 0 ? (
        <LoadingMessage>{t('playerKillRanking.loading')}</LoadingMessage>
      ) : (
        <StyledTable>
          <thead>
            <tr>
              <Th>{t('playerKillRanking.th.rank')}</Th>
              <Th>{t('playerKillRanking.th.characterName')}</Th>
              <Th>{t('playerKillRanking.th.guild')}</Th>
              <Th>{t('playerKillRanking.th.level')}</Th>
              <Th>{t('playerKillRanking.th.percent')}</Th>
              <Th>{t('playerKillRanking.th.pk')}</Th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((player, index) => (
              <tr key={index}>
                <Td>{getMedal(index + 1)}</Td>
                <Td>
                  <JobIcon
                    src={player.job}
                    alt={t('playerKillRanking.jobAlt', {job: player.job})}
                    onError={e => {
                      e.target.onerror = null;
                      e.target.src = '/assets/jobs/000.jpg';
                    }}
                  />
                  {player.name}
                </Td>
                <Td>
                  {player.gilde ? (
                    <GuildContainer>
                      {player.icon ? (
                        <GuildIcon
                          src={`/assets/gicons/${player.icon}`}
                          alt="Guild Icon"
                          onError={e => {
                            e.target.onerror = null;
                            e.target.remove();
                          }}
                        />
                      ) : null}
                      {player.gilde}
                    </GuildContainer>
                  ) : (
                    ''
                  )}
                </Td>
                <Td>{player.lv}</Td>
                <Td>{player.exp_procent} %</Td>
                <Td>{player.pkc}</Td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      )}
    </RankingContainer>
  );
};

export default PlayerKillRanking;

const RankingContainer = styled.div`
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 20px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  font-size: 1.2rem;
`;

const StyledTable = styled.table.attrs({className: 'table-striped'})`
  width: 100%;
  border-color: #261d12 !important;
  border-collapse: collapse;
  &.table-striped > tbody > tr:nth-of-type(odd) {
    background-color: rgb(245 64 114 / 12%);
  }
`;

const Th = styled.th`
  padding: 10px;
  text-align: left;
`;

const Td = styled.td`
  padding: 10px;
`;

const JobIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  margin-right: 5px;
`;

const GuildContainer = styled.div`
  display: flex;
  align-items: center;
`;

const GuildIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: cover;
  margin-right: 5px;
`;
